import { dateOfISO } from "../../shared/planDates";
import {
  PNOE_WORKOUT_NAME,
  PNOE_WORKOUT_NAME_OTHER,
  WORKOUT_TYPE,
  workoutTypeLabel,
} from "../model/workoutEnums";
import {
  Activity,
  ActivityEvent,
  ScheduledService,
  WearableSession,
  WorkoutDay,
  WorkoutWeek,
} from "../model/workoutPlanTypes";

/**
 * Pure selectors over the assembled week, mirroring the rules the mobile
 * `WorkoutStore.normalizeResponse` and `ActivityCard` apply before drawing.
 */

export const isScheduledActivity = (
  entry: Activity | ActivityEvent
): entry is Activity => entry.is_scheduled === true;

export const isRestDay = (activity: Activity): boolean =>
  activity.type === WORKOUT_TYPE.RestDay;

/** Services whose session starts or ends on `date` (yyyy-MM-dd). */
export const servicesOnDay = (
  services: ScheduledService[],
  date: string
): ScheduledService[] =>
  services.filter(
    (service) =>
      dateOfISO(service.startTime) === date || dateOfISO(service.endTime) === date
  );

export type DayEntries = {
  scheduled: Activity[];
  events: ActivityEvent[];
  services: ScheduledService[];
};

/**
 * What a day row shows, in order: planned workouts, then wearable events,
 * then coached services. A Rest Day is dropped when the day holds a wearable
 * event — a recorded activity supersedes the rest day (mobile rule).
 */
export const dayEntries = (
  day: WorkoutDay,
  services: ScheduledService[]
): DayEntries => {
  const activities = day.activities ?? [];
  const events = activities.filter(
    (entry): entry is ActivityEvent => !isScheduledActivity(entry)
  );
  const scheduled = activities
    .filter(isScheduledActivity)
    .filter((activity) => !(events.length > 0 && isRestDay(activity)));
  return { scheduled, events, services: servicesOnDay(services, day.day) };
};

/**
 * The name a planned workout is shown under: the PNOE workout-name table,
 * except "Other Workout" (26) and ids the table does not know, which use the
 * entry's `canonical_name`; a nameless workout falls back to its type.
 */
export const activityDisplayName = (activity: Activity): string => {
  const canonical = activity.canonical_name?.trim();
  const named = PNOE_WORKOUT_NAME[activity.name];
  if (named && activity.name !== PNOE_WORKOUT_NAME_OTHER) return named;
  if (canonical) return canonical;
  if (isRestDay(activity)) return "Rest Day";
  return workoutTypeLabel(activity.type);
};

/**
 * The name of a wearable entry or session, as the mobile `getWorkoutName`
 * resolves it: the PNOE workout-name table, except "Other Workout" (26) and
 * unknown ids, which use the entry's `canonical_name` (the provider's own
 * label, often lower-cased).
 */
export const eventDisplayName = (event: ActivityEvent | WearableSession): string => {
  const named = event.name !== undefined ? PNOE_WORKOUT_NAME[event.name] : undefined;
  if (named && event.name !== PNOE_WORKOUT_NAME_OTHER) return named;
  return event.canonical_name?.trim() || named || "Activity";
};

export type ActivityProgress = "not_started" | "in_progress" | "completed";

/**
 * Derived from calories, as the mobile `ActivityCard` does (the `status`
 * enum is not what the app's badges read): burned at or past the target is
 * completed, anything burned short of it is in progress.
 */
export const activityProgress = (activity: Activity): ActivityProgress => {
  const target = activity.cals?.target ?? 0;
  const current = activity.cals?.current ?? 0;
  if (!target) return "not_started";
  if (current >= target) return "completed";
  if (current > 0) return "in_progress";
  return "not_started";
};

/** Days of the week in calendar order; the backend does not promise an order or seven entries. */
export const sortedDays = (week: WorkoutWeek): WorkoutDay[] =>
  [...(week.daily ?? [])].sort((a, b) => a.day.localeCompare(b.day));

/**
 * Whether the week holds a plan at all: at least one planned entry, rest days
 * included. Deliberately stricter than the mobile "any entry" check so a
 * member who only syncs a wearable keeps the report-based tab.
 */
export const weekHasPlan = (week: WorkoutWeek | null | undefined): boolean =>
  !!week?.daily?.some((day) => (day.activities ?? []).some(isScheduledActivity));

/** "x / y Workouts completed" over the planned, non-rest workouts of the week. */
export const weekCompletion = (
  week: WorkoutWeek
): { done: number; total: number } => {
  const workouts = (week.daily ?? [])
    .flatMap((day) => day.activities ?? [])
    .filter(isScheduledActivity)
    .filter((activity) => !isRestDay(activity));
  return {
    done: workouts.filter((activity) => activityProgress(activity) === "completed")
      .length,
    total: workouts.length,
  };
};

export const findActivity = (
  week: WorkoutWeek | undefined,
  date: string,
  id: string
): Activity | undefined =>
  week?.daily
    ?.find((day) => day.day === date)
    ?.activities?.filter(isScheduledActivity)
    .find((activity) => activity.id === id);

export const findEvent = (
  week: WorkoutWeek | undefined,
  date: string,
  id: string
): ActivityEvent | undefined =>
  week?.daily
    ?.find((day) => day.day === date)
    ?.activities?.filter(
      (entry): entry is ActivityEvent => !isScheduledActivity(entry)
    )
    .find((event) => event.id === id);

export const findService = (
  week: WorkoutWeek | undefined,
  id: string
): ScheduledService | undefined =>
  week?.services?.find((service) => service.id === id);

/** Whole minutes for display ("38.18" → "38"). */
export const formatMinutes = (minutes: number | undefined | null): string =>
  `${Math.round(minutes ?? 0)} min`;

/**
 * "HH:mm" of a datetime string. ISO forms go through `Date` (browser
 * timezone); anything it rejects — seven-digit fractions, a bare
 * "HH:mm:ss", a space instead of the "T" — falls back to the clock digits
 * in the string, as luxon's `fromISO` would read them.
 */
export const formatClockTime = (iso: string | undefined | null): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (!Number.isNaN(date.getTime())) {
    return `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}`;
  }
  const match = iso.match(/(?:^|[T ])(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : "";
};

/**
 * The target HR zone of a planned workout, for Interval and Cardio only: the
 * contract's `zone`, else the type-specific extras the samples carry. 0 when
 * not applicable.
 */
export const activityZone = (activity: Activity): number => {
  if (
    activity.type !== WORKOUT_TYPE.Interval &&
    activity.type !== WORKOUT_TYPE.Cardio
  ) {
    return 0;
  }
  return (
    activity.zone ||
    activity.interval_work_zone ||
    activity.cardio_work_zone ||
    0
  );
};

/** Sessions merged into a tracked activity (mobile `getSessionNumber`). */
export const eventSessionCount = (event: ActivityEvent): number =>
  event.events?.length ?? 0;

/** "(1 Session)" / "(2 Sessions)", or "" when the entry carries no sessions. */
export const formatSessionCount = (event: ActivityEvent): string => {
  const count = eventSessionCount(event);
  if (count === 0) return "";
  return count === 1 ? "(1 Session)" : `(${count} Sessions)`;
};

/** "(2 Sess.)", for a card too narrow for `formatSessionCount`. */
export const formatSessionCountShort = (event: ActivityEvent): string => {
  const count = eventSessionCount(event);
  return count === 0 ? "" : `(${count} Sess.)`;
};

/**
 * The wearable events a planned workout absorbed: those on its day whose
 * `reference.activity_id` points at it. Shown as the workout's "Wearable
 * Data", as the mobile details screen does with the record's `events`.
 */
export const linkedEvents = (
  day: WorkoutDay | undefined,
  activityId: string
): ActivityEvent[] =>
  (day?.activities ?? []).filter(
    (entry): entry is ActivityEvent =>
      !isScheduledActivity(entry) && entry.reference?.activity_id === activityId
  );

/** Zone shown on a card: Cardio / Interval only, and only when the entry carries one. */
export const cardZone = (entry: Activity | ActivityEvent): number | undefined => {
  if (entry.type !== WORKOUT_TYPE.Cardio && entry.type !== WORKOUT_TYPE.Interval) {
    return undefined;
  }
  if (isScheduledActivity(entry)) {
    const zone = activityZone(entry);
    return entry.zone === undefined && !zone ? undefined : zone;
  }
  return entry.zone;
};

/** Calories burned in a tracked activity or session, whichever shape the backend sent. */
export const eventCalories = (event: ActivityEvent | WearableSession): number => {
  const cals = event.cals;
  if (typeof cals === "number") return cals;
  return cals?.current ?? 0;
};

/**
 * The tag the mobile details screen shows once a workout has a merged
 * session: "Automatically Logged" from a wearable (input_source ≠ 0),
 * "Manually Logged" otherwise; nothing without sessions.
 */
export const loggedStateLabel = (
  events: Array<ActivityEvent | WearableSession>
): string | null => {
  if (events.length === 0) return null;
  return events[0].input_source !== 0 ? "Automatically Logged" : "Manually Logged";
};

/**
 * The sessions behind tracked entries, for the "Wearable Data" widgets: an
 * entry's merged `events` when it has them (the weekly overview's shape,
 * which the mobile detail screen reads), else the entry itself.
 */
export const wearableSessions = (
  entries: Array<ActivityEvent | WearableSession>
): WearableSession[] =>
  entries.flatMap((entry) =>
    "events" in entry && entry.events?.length ? entry.events : [entry as WearableSession]
  );
